import type { QueryClient } from '@tanstack/react-query';
import { createRouter, type RouterHistory } from '@tanstack/react-router';

import { routeTree } from '@/routeTree.gen';
import { PageLoader } from '@/shared/ui/loader';

import { RouteError } from './route-error';
import { NotFound } from './not-found';

export type RouterContext = {
  queryClient: QueryClient;
};

/**
 * 라우터 — 로더는 TanStack Query 캐시에 데이터를 채우고(ensureQueryData) 화면은 useSuspenseQuery 로 읽는다.
 * 링크에 마우스를 올리면 미리 불러오고(preload: intent), 캐시 신선도 판단은 Query 에 맡긴다.
 */
export function createAppRouter(
  queryClient: QueryClient,
  options: { history?: RouterHistory } = {},
) {
  return createRouter({
    routeTree,
    context: { queryClient },
    ...(options.history ? { history: options.history } : {}),
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: () => <PageLoader />,
    defaultPendingMs: 300,
    defaultPendingMinMs: 300,
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: NotFound,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- 선언 병합은 interface 로만 된다
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- 선언 병합은 interface 로만 된다
  interface StaticDataRouteOption {
    /** 헤더 브레드크럼·문서 제목에 쓰는 화면 이름 */
    title?: string;
    /** 사이드바 그룹 이름 */
    group?: string;
    /** 모바일 하단 탭바를 숨긴다 — 하단 고정 CTA 가 있는 화면 */
    hideTabBar?: boolean;
  }
}
